import React, { useState, useEffect, useCallback } from 'react';
import { Box, Paper, SelectChangeEvent, Snackbar, Alert } from '@mui/material';
import {
  ListPageScaffold,
  ListPageMain,
  ListPageToolbar,
  ListPageFiltersSection,
} from '../../components/common/list-page';
import { PhysicalCountFilters } from '../../components/inventory/PhysicalCountFilters';
import { PhysicalCountActionButtons } from '../../components/inventory/PhysicalCountActionButtons';
import { PhysicalCountTable } from '../../components/inventory/PhysicalCountTable';
import { PhysicalCountPanel } from '../../components/inventory/PhysicalCountPanel';
import { PhysicalCountDetailModal } from '../../components/inventory/PhysicalCountDetailModal';
import { PhysicalCountWriteOffMissingModal } from '../../components/inventory/PhysicalCountWriteOffMissingModal';
import CommonPagination from '../../components/common/Pagination';
import { LocationsService } from '../../api/services/LocationsService';
import { Location } from '../../api/models/Location';
import { PhysicalCountService, mapSessionFromApi } from '../../api/physicalCountCompat';
import type {
  PhysicalCountSession,
  PhysicalCountFilterState,
} from '../../types/physicalCount.types';
const PhysicalCountPage: React.FC = () => {
  const [sessions, setSessions] = useState<PhysicalCountSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState<Array<{ id: number; name: string }>>([]);
  const emptyFilters = (): PhysicalCountFilterState => ({
    code: '',
    locationId: '',
  });
  const [filters, setFilters] = useState<PhysicalCountFilterState>(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState<PhysicalCountFilterState>(emptyFilters);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 1,
  });
  const [panelOpen, setPanelOpen] = useState(false);
  const [viewSession, setViewSession] = useState<PhysicalCountSession | null>(null);
  const [writeOffSession, setWriteOffSession] = useState<PhysicalCountSession | null>(null);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadLocations = useCallback(async () => {
    try {
      const res = await LocationsService.locationsControllerFindAll();
      const data = (res as { data?: Location[] })?.data ?? (Array.isArray(res) ? res : []);
      const branches = data.filter((l: Location) => l.type === Location.type.BRANCH);
      setLocations(
        branches.map((l: Location) => ({
          id: l.id,
          name: l.name ?? 'Sin nombre',
        })),
      );
    } catch {
      setLocations([]);
    }
  }, []);

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await PhysicalCountService.sessionsFindAll({
        code: appliedFilters.code.trim() !== '' ? appliedFilters.code.trim() : undefined,
        locationId:
          appliedFilters.locationId !== '' ? Number(appliedFilters.locationId) : undefined,
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
      });
      const mapped = res.data.map(mapSessionFromApi);
      setSessions(mapped);
      setPagination((prev) => ({
        ...prev,
        totalItems: res.meta.total,
        totalPages: res.meta.totalPages,
      }));
    } catch {
      setSessions([]);
      setPagination((prev) => ({ ...prev, totalItems: 0, totalPages: 1 }));
    } finally {
      setLoading(false);
    }
  }, [
    appliedFilters.code,
    appliedFilters.locationId,
    pagination.currentPage,
    pagination.itemsPerPage,
  ]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleSearch = useCallback(() => {
    setAppliedFilters(filters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [filters]);

  const handleReset = useCallback(() => {
    const empty = emptyFilters();
    setFilters(empty);
    setAppliedFilters(empty);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const handleClearAppliedField = useCallback((field: keyof PhysicalCountFilterState) => {
    setFilters((prev) => ({ ...prev, [field]: '' }));
    setAppliedFilters((prev) => ({ ...prev, [field]: '' }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const handleNewSession = useCallback(() => {
    setPanelOpen(true);
  }, []);

  const handleExport = useCallback(async (locationId: number) => {
    try {
      await PhysicalCountService.exportExpectedInventory(locationId);
    } catch {
      // Error handling could show toast
    }
  }, []);

  const handleViewSession = useCallback((session: PhysicalCountSession) => {
    setViewSession(session);
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  const handleRequestWriteOffSession = useCallback((session: PhysicalCountSession) => {
    setWriteOffSession(session);
  }, []);

  const handleWriteOffNotify = useCallback(
    (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
      setNotification({ open: true, message, severity });
    },
    [],
  );

  const handlePageChange = useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  }, []);

  const handleItemsPerPageChange = useCallback((event: SelectChangeEvent<unknown>) => {
    const itemsPerPage = Number(event.target.value);
    setPagination((prev) => ({
      ...prev,
      itemsPerPage,
      currentPage: 1,
    }));
  }, []);

  return (
    <ListPageScaffold>
      <ListPageMain sectionGap={1}>
        <ListPageToolbar>
          <PhysicalCountActionButtons
            onNewSession={handleNewSession}
            onExport={handleExport}
            locations={locations}
          />
        </ListPageToolbar>

        <ListPageFiltersSection>
          <PhysicalCountFilters
            filters={filters}
            setFilters={setFilters}
            appliedFilters={appliedFilters}
            onClearAppliedField={handleClearAppliedField}
            onSearch={handleSearch}
            onReset={handleReset}
            locations={locations}
          />
        </ListPageFiltersSection>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            flex: 1,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              backgroundImage: 'none',
              overflow: 'hidden',
            }}
          >
            <PhysicalCountTable
              sessions={sessions}
              loading={loading}
              onViewSession={handleViewSession}
              onRequestWriteOffSession={handleRequestWriteOffSession}
            />
          </Paper>

          {pagination.totalItems > 0 && (
            <Box sx={{ p: 2 }}>
              <CommonPagination
                currentPage={pagination.currentPage}
                itemsPerPage={pagination.itemsPerPage}
                totalItems={pagination.totalItems}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </Box>
          )}
        </Box>
      </ListPageMain>

      <PhysicalCountPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        onSuccess={loadSessions}
        locations={locations}
      />

      <PhysicalCountDetailModal
        open={viewSession != null}
        onClose={() => setViewSession(null)}
        session={viewSession}
      />

      <PhysicalCountWriteOffMissingModal
        open={writeOffSession != null}
        onClose={() => setWriteOffSession(null)}
        session={writeOffSession}
        onSuccess={loadSessions}
        onNotify={handleWriteOffNotify}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </ListPageScaffold>
  );
};

export default PhysicalCountPage;
